import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { type TableColumn } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Dropdown, type DropdownItem } from '../../components/ui/Dropdown';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Modal } from '../../components/ui/Modal';
import { FormField } from '../../components/ui/FormField';
import { userManagementService, getUserLoginIps } from '../../services/admin';
import type { UserLoginIp } from '../../services/admin/userManagement';
import type { AdminUser, UserListParams, UpdateUserData } from '../../types/admin';

/**
 * User Management Page (Admin Only)
 *
 * Provides system administrators with tools to:
 * - View all users with pagination and filtering
 * - Search users by name or email
 * - Filter by role and status
 * - Edit, disable, enable, or delete users
 * - View user account associations
 */
export const UserManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination & Filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'admin' | 'user' | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'disabled' | ''>('');
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<'under_trial' | 'trial_expired' | 'paid' | ''>('');

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; userId: number | null; userName: string | null }>({
    open: false,
    userId: null,
    userName: null,
  });

  // Edit modal state
  const [editModal, setEditModal] = useState<{ open: boolean; user: AdminUser | null }>({
    open: false,
    user: null,
  });
  const [editFormData, setEditFormData] = useState<UpdateUserData>({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  // IP modal state
  const [ipModalUser, setIpModalUser] = useState<AdminUser | null>(null);
  const [loginIps, setLoginIps] = useState<UserLoginIp[]>([]);
  const [ipsLoading, setIpsLoading] = useState(false);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: UserListParams = {
        page: currentPage,
        per_page: 25,
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(subscriptionStatusFilter && { subscription_status: subscriptionStatusFilter }),
      };

      const response = await userManagementService.getAllUsers(params);
      setUsers(response.data);
      setTotalPages(response.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchUsers();
  }, [currentPage, search, roleFilter, statusFilter, subscriptionStatusFilter]);

  // Handle user actions
  const handleDisableUser = async (id: number) => {
    try {
      await userManagementService.disableUser(id);
      setSuccess(t('user_management.messages.user_disabled'));
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.disable_error'));
    }
  };

  const handleEnableUser = async (id: number) => {
    try {
      await userManagementService.enableUser(id);
      setSuccess(t('user_management.messages.user_enabled'));
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.enable_error'));
    }
  };

  const handleVerifyEmail = async (id: number) => {
    try {
      await userManagementService.verifyEmail(id);
      setSuccess(t('user_management.messages.email_verified'));
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.verify_error'));
    }
  };

  const handleDeleteUser = async (id: number, name: string) => {
    setDeleteConfirm({ open: true, userId: id, userName: name });
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirm.userId) return;

    try {
      await userManagementService.deleteUser(deleteConfirm.userId);
      setSuccess(t('user_management.messages.user_deleted'));
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.delete_error'));
    }
  };

  const handleEditUser = (user: AdminUser) => {
    setEditModal({ open: true, user });
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editModal.user) return;

    try {
      setEditSubmitting(true);
      setError(null);
      await userManagementService.updateUser(editModal.user.id, editFormData);
      setSuccess(t('user_management.messages.user_updated'));
      setEditModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user_management.messages.update_error'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleOpenIpModal = async (user: AdminUser) => {
    setIpModalUser(user);
    setLoginIps([]);
    setIpsLoading(true);
    try {
      const ips = await getUserLoginIps(user.id);
      setLoginIps(ips);
    } catch {
      // silently fail — modal will show empty state
    } finally {
      setIpsLoading(false);
    }
  };

  // Table columns
  const columns: TableColumn<AdminUser>[] = [
    {
      key: 'id',
      header: t('user_management.table.id'),
    },
    {
      key: 'name',
      header: t('user_management.table.name'),
    },
    {
      key: 'email',
      header: t('user_management.table.email'),
    },
    {
      key: 'disabled_at',
      header: t('user_management.table.status'),
      render: (value) => (
        <Badge variant={value ? 'error' : 'success'}>
          {value ? t('user_management.status_disabled') : t('user_management.status_active')}
        </Badge>
      ),
    },
    {
      key: 'telegram',
      header: 'Telegram',
      render: (value, row) => {
        if (!value) {
          return <span className="text-sm text-gray-400">—</span>;
        }

        const verified = (row as AdminUser).telegram_verified;

        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">@{String(value).replace(/^@/, '')}</span>
            {verified === false && (
              <Badge variant="warning">Not validated</Badge>
            )}
            {verified === null && (
              <Badge variant="default">Checking…</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'email_verified_at',
      header: t('user_management.table.verified'),
      render: (value) => (
        <Badge variant={value ? 'success' : 'warning'}>
          {value ? t('user_management.table.verified_yes') : t('user_management.table.verified_no')}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Registered',
      render: (value) => value ? (
        <span className="text-sm text-gray-700 whitespace-nowrap">
          {new Date(String(value)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ) : <span className="text-gray-400">—</span>,
    },
    {
      key: 'account_id' as keyof AdminUser,
      header: 'Plan',
      render: (_value, row) => {
        const account = (row as AdminUser).account;
        if (!account) return <Badge variant="default">No account</Badge>;

        const plan = account.subscription_plan;
        const now = new Date();
        const endsAt = account.subscription_ends_at ? new Date(account.subscription_ends_at) : null;
        const isActive = endsAt ? endsAt > now : false;
        const isTrialPlan = plan?.slug === 'free-trial';
        const trialUsed = !!account.free_trial_used_at;

        if (plan && isActive && isTrialPlan) {
          const until = endsAt ? ` until ${endsAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : '';
          return <Badge variant="warning">Trial{until}</Badge>;
        }

        if (plan && isActive) {
          const until = endsAt ? ` until ${endsAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : '';
          return <Badge variant="success">{plan.name}{until}</Badge>;
        }

        if (trialUsed) {
          return <Badge variant="default">Free (trial used)</Badge>;
        }

        return <Badge variant="default">Free</Badge>;
      },
    },
  ];

  // Actions dropdown for each user
  const renderActions = (user: AdminUser) => {
    const items: DropdownItem[] = [
      {
        id: 'edit',
        label: t('user_management.actions.edit'),
        onClick: () => handleEditUser(user),
      },
      {
        id: 'toggle-status',
        label: user.disabled_at ? t('user_management.actions.enable') : t('user_management.actions.disable'),
        onClick: () =>
          user.disabled_at ? handleEnableUser(user.id) : handleDisableUser(user.id),
      },
    ];

    // Add verify email option if not verified
    if (!user.email_verified_at) {
      items.push({
        id: 'verify-email',
        label: t('user_management.actions.verify_email'),
        onClick: () => handleVerifyEmail(user.id),
      });
    }

    // Add delete option
    items.push({
      id: 'delete',
      label: t('user_management.actions.delete'),
      onClick: () => handleDeleteUser(user.id, user.name),
      danger: true,
      divider: true,
    });

    return (
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); handleOpenIpModal(user); }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          IPs
        </button>
        <Dropdown
          trigger={
            <Button variant="secondary" size="sm">
              {t('user_management.actions.label')}
            </Button>
          }
          items={items}
          align="right"
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('user_management.page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('user_management.page_subtitle')}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('user_management.search_label')}
            </label>
            <Input
              id="search"
              placeholder={t('user_management.search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Role Filter */}
          <div>
            <label htmlFor="role-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('user_management.role_filter_label')}
            </label>
            <select
              id="role-filter"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as 'admin' | 'user' | '');
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('user_management.role_filter_all')}</option>
              <option value="admin">{t('user_management.role_admin')}</option>
              <option value="user">{t('user_management.role_user')}</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('user_management.status_filter_label')}
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'disabled' | '');
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('user_management.status_filter_all')}</option>
              <option value="active">{t('user_management.status_active')}</option>
              <option value="disabled">{t('user_management.status_disabled')}</option>
            </select>
          </div>

          {/* Subscription Status Filter */}
          <div>
            <label htmlFor="subscription-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('user_management.subscription_status_filter_label')}
            </label>
            <select
              id="subscription-status-filter"
              value={subscriptionStatusFilter}
              onChange={(e) => {
                setSubscriptionStatusFilter(e.target.value as 'under_trial' | 'trial_expired' | 'paid' | '');
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('user_management.subscription_status_filter_all')}</option>
              <option value="under_trial">{t('user_management.subscription_status_under_trial')}</option>
              <option value="trial_expired">{t('user_management.subscription_status_trial_expired')}</option>
              <option value="paid">{t('user_management.subscription_status_paid')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={users}
              renderActions={renderActions}
              emptyMessage={t('user_management.table.empty_message')}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Edit User Modal */}
      {editModal.open && editModal.user && (
        <Modal
          open={editModal.open}
          onClose={() => setEditModal({ open: false, user: null })}
          title={t('user_management.actions.edit')}
          size="medium"
        >
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {/* Name */}
            <FormField
              label={t('user_management.table.name')}
              required
              htmlFor="edit-name"
            >
              <Input
                id="edit-name"
                type="text"
                value={editFormData.name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                placeholder={t('user_management.table.name')}
                required
                disabled={editSubmitting}
              />
            </FormField>

            {/* Email */}
            <FormField
              label={t('user_management.table.email')}
              required
              htmlFor="edit-email"
            >
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                placeholder={t('user_management.table.email')}
                required
                disabled={editSubmitting}
              />
            </FormField>

            {/* Role */}
            <FormField
              label={t('user_management.table.role')}
              required
              htmlFor="edit-role"
            >
              <select
                id="edit-role"
                value={editFormData.role || 'user'}
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value as 'admin' | 'user' })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={editSubmitting}
              >
                <option value="user">{t('user_management.role_user')}</option>
                <option value="admin">{t('user_management.role_admin')}</option>
              </select>
            </FormField>

            {/* Form Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditModal({ open: false, user: null })}
                disabled={editSubmitting}
              >
                {t('user_management.actions.cancel')}
              </Button>
              <Button type="submit" disabled={editSubmitting} loading={editSubmitting}>
                {t('user_management.actions.save')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, userId: null, userName: null })}
        onConfirm={confirmDeleteUser}
        title={t('user_management.actions.delete')}
        message={t('user_management.actions.delete_confirm')}
        description={deleteConfirm.userName ? `User: ${deleteConfirm.userName}` : undefined}
        confirmText={t('user_management.actions.delete')}
        variant="danger"
      />

      {/* Login IPs Modal */}
      {ipModalUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIpModalUser(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Login IPs — {ipModalUser.name}</h3>
              <button onClick={() => setIpModalUser(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            {ipsLoading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : loginIps.length === 0 ? (
              <p className="text-gray-500 text-sm">No login records yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase">
                    <th className="pb-2 pr-4">IP Address</th>
                    <th className="pb-2 pr-4">Fingerprint</th>
                    <th className="pb-2 pr-4">Logins</th>
                    <th className="pb-2 pr-4">Event</th>
                    <th className="pb-2">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {loginIps.map((ip) => (
                    <tr key={ip.ip_address} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono">{ip.ip_address}</td>
                      <td className="py-2 pr-4 font-mono text-gray-500">
                        {ip.fingerprint
                          ? <span title={ip.fingerprint}>{ip.fingerprint.slice(0, 12)}…</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2 pr-4">{ip.login_count}</td>
                      <td className="py-2 pr-4 capitalize">{ip.event}</td>
                      <td className="py-2 text-gray-500">{new Date(ip.last_seen_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

UserManagementPage.displayName = 'UserManagementPage';
